/**
 * Translations, kept as small as they can be.
 *
 * English text is the key: `t('Go live')` is English as written, and other
 * languages map that exact string to theirs. A missing entry falls back to the
 * English, so a new string is never blank, only untranslated. `{name}`
 * placeholders are filled from the second argument. A third argument names a
 * context for words that translate differently in different places (`preset|Light`).
 *
 * The language is a preference (`prefs.language`) that defaults from the
 * browser, so a friend opening the viewer link in a Portuguese browser gets
 * Portuguese without any setting.
 */

import { prefs, type Language } from './prefs.svelte'

export type { Language }

export const LANGUAGES: { value: Language; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'pt-BR', label: 'Português (Brasil)' },
]

const ptBR: Record<string, string> = {
  'Check your email and password': 'Confira seu e-mail e sua senha',
  'Those details did not match an account. Check them and try again.':
    'Os dados não correspondem a uma conta. Confira e tente novamente.',
  'Confirm your email first': 'Confirme seu e-mail primeiro',
  'Open the confirmation email, then return to Sofa and sign in again.':
    'Abra o e-mail de confirmação, volte ao Sofa e entre novamente.',
  'Sign-in was cancelled': 'A entrada na conta foi cancelada',
  'You can try signing in again whenever you are ready.':
    'Você pode tentar entrar novamente quando quiser.',
  'Account connection needs attention': 'A conexão com sua conta precisa de atenção',
  'Check your internet connection and try again. If your session expired, sign in again to reconnect your friends list.':
    'Confira sua conexão com a internet e tente novamente. Se a sessão expirou, entre de novo para reconectar sua lista de amigos.',
  'Refresh page': 'Recarregar página',
  'Finish sharing before updating': 'Encerre o compartilhamento antes de atualizar',
  'Stop your current share, then choose Update and restart.':
    'Pare o compartilhamento atual e selecione Atualizar e reiniciar.',
  'The update could not finish': 'Não foi possível concluir a atualização',
  'Check your connection and try again from Settings. You can keep using your current version.':
    'Confira sua conexão e tente novamente nas Configurações. Você pode continuar usando a versão atual.',
  'Check your friend’s details': 'Confira os dados do seu amigo',
  'Preview unavailable': 'Prévia indisponível',
  'Sofa could not show the local preview. This does not necessarily mean your friends lost the stream.':
    'O Sofa não conseguiu exibir a prévia local. Isso não significa necessariamente que seus amigos perderam a transmissão.',
  'Room unavailable': 'Sala indisponível',
  'Could not complete sign-in': 'Não foi possível entrar na conta',
  'Check your internet connection and try signing in again. If your session expired, sign in again to reconnect your friends list.':
    'Confira sua conexão com a internet e tente entrar novamente. Se a sessão expirou, entre de novo para reconectar sua lista de amigos.',
  'Try a different value and save again. If it keeps failing, restart Sofa and try again.':
    'Tente outro valor e salve novamente. Se continuar falhando, reinicie o Sofa e tente de novo.',
  'The room is taking too long to respond': 'A sala está demorando para responder',
  'Try again, or ask your friend to check that sharing is still running and send their current link.':
    'Tente novamente ou peça ao seu amigo para conferir se ainda está compartilhando e enviar o link atual.',
  'The host ended this share': 'O anfitrião encerrou o compartilhamento',
  'Thanks for watching. If your friend shares again, join from the friends list or ask for their new link.':
    'Obrigado por assistir. Quando seu amigo compartilhar de novo, entre pela lista de amigos ou peça o novo link.',
  'This room is no longer available': 'Esta sala não está mais disponível',
  'The share may have ended or the link may have changed. Open your friends list or ask the host for a new link.':
    'O compartilhamento pode ter terminado ou o link pode ter mudado. Abra sua lista de amigos ou peça um novo link ao anfitrião.',
  'Reconnecting to your friend…': 'Reconectando ao seu amigo…',
  'The connection was interrupted. Sofa is trying again and will follow a new share link when your friend comes back.':
    'A conexão foi interrompida. O Sofa está tentando novamente e usará o novo link quando seu amigo voltar.',
  'The video could not connect': 'Não foi possível conectar o vídeo',
  'Try reconnecting. If it keeps happening, ask the host to restart sharing and send the new link.':
    'Tente reconectar. Se continuar acontecendo, peça ao anfitrião para reiniciar o compartilhamento e enviar o novo link.',
  'Wrong password': 'Senha incorreta',
  'Check the password with the host, then enter it below and join again.':
    'Confirme a senha com o anfitrião, digite-a abaixo e entre novamente.',
  'Check the password with the host, then try again.':
    'Confirme a senha com o anfitrião e tente novamente.',
  'The room is full': 'A sala está cheia',
  'All seats are taken. Try again when someone leaves, or ask the host to increase the viewer limit.':
    'Todos os lugares estão ocupados. Tente novamente quando alguém sair ou peça ao anfitrião para aumentar o limite de espectadores.',
  'An update is needed': 'É necessário atualizar',
  'Update Sofa, or refresh this page in your browser. The host may need to update too.':
    'Atualize o Sofa ou recarregue esta página no navegador. O anfitrião também pode precisar atualizar.',
  'Too many attempts': 'Muitas tentativas',
  'Wait a moment before joining again.': 'Aguarde um pouco antes de tentar entrar novamente.',
  'You cannot join this room': 'Não foi possível entrar nesta sala',
  'Ask the host for permission and their current share link.':
    'Peça ao anfitrião permissão e o link atual do compartilhamento.',
  'Could not join the room': 'Não foi possível entrar na sala',
  'Try again. If the problem continues, ask the host for their current share link.':
    'Tente novamente. Se o problema continuar, peça ao anfitrião o link atual do compartilhamento.',
  'Back to home': 'Voltar ao início',
  'Back to friends': 'Voltar aos amigos',
  'Technical details': 'Detalhes técnicos',
  'Share ended': 'Compartilhamento encerrado',
  'Ready to join': 'Pronto para entrar',
  'Check the room link': 'Confira o link da sala',
  'Could not save this setting': 'Não foi possível salvar esta configuração',
  'Account needs attention': 'Sua conta precisa de atenção',
  'Check your sharing settings': 'Confira as configurações de compartilhamento',
  'The shared window is unavailable': 'A janela compartilhada está indisponível',
  'Open the window again and select it in Sofa to continue sharing.':
    'Abra a janela novamente e selecione-a no Sofa para continuar compartilhando.',
  'Sharing could not connect': 'Não foi possível conectar o compartilhamento',
  'Check your internet connection, then try again. You can also choose another connection mode in stream settings.':
    'Confira sua conexão com a internet e tente novamente. Você também pode escolher outro modo de conexão nas configurações da transmissão.',
  'Sofa needs permission': 'O Sofa precisa de permissão',
  'Check screen and microphone permissions in your system settings, then try again.':
    'Confira as permissões de tela e microfone nas configurações do sistema e tente novamente.',
  'Sharing needs attention': 'O compartilhamento precisa de atenção',
  'Try the action again. If it keeps failing, choose the window again or restart sharing.':
    'Tente novamente. Se continuar falhando, selecione a janela de novo ou reinicie o compartilhamento.',
  'Use a room code or a full HTTPS room link.':
    'Use um código de sala ou o link HTTPS completo do compartilhamento.',
  'Use a room code or a link ending in /r/CODE.':
    'Use um código de sala ou um link que termine em /r/CÓDIGO.',
  'The room link does not match the room code.':
    'O link não corresponde ao código da sala. Peça o link atual ao anfitrião.',
  'Invalid signaling endpoint.':
    'O endereço da sala é inválido. Peça o link completo ao anfitrião.',
  'The room link contains an invalid escape sequence.':
    'O link da sala está incompleto ou inválido. Copie o link completo e tente novamente.',
  'Room links must use HTTPS and cannot contain credentials.':
    'Use o link HTTPS do compartilhamento, sem nome de usuário ou senha no endereço.',
  'This link points to the desktop app, not a room server. Ask the host for their current share link.':
    'Este link aponta para o aplicativo, não para a sala. Peça ao anfitrião o link atual do compartilhamento.',
  'HTTP rooms require verified LAN transport. Use an HTTPS link.':
    'Este link só funciona na rede local. Peça ao anfitrião um link HTTPS.',
  'The room link must end in /r/CODE.':
    'O link deve terminar em /r/CÓDIGO. Copie o link completo do compartilhamento.',
  'Invalid room code. Example: K7M2QX.': 'Código de sala inválido. Exemplo: K7M2QX.',
  'Your login is not valid any more. Sign in again.': 'Sua sessão expirou. Entre novamente.',
  'This server cannot check logins right now.':
    'Não foi possível verificar sua conta agora. Tente novamente em alguns instantes.',
  'Another Sofa connection replaced this one. Reconnect here to use friends.':
    'Outra conexão do Sofa substituiu esta. Reconecte aqui para usar a lista de amigos.',
  'You signed in on another device, so this one was disconnected.':
    'Você entrou em outro dispositivo. Entre novamente aqui para usar a lista de amigos.',
  'Checking sharing modes…': 'Verificando modos de compartilhamento…',
  'Could not check sharing availability.':
    'Não foi possível verificar os modos de compartilhamento.',
  'Check again': 'Verificar novamente',
  Unavailable: 'Indisponível',
  'Choose a connection': 'Escolha uma conexão',
  'Connection settings': 'Configurações de conexão',
  'Share through the Sofa server. Requires signing in.':
    'Compartilhe pelo servidor Sofa. É necessário entrar na conta.',
  'Sign in to use the Sofa server. P2P and Local network work without an account.':
    'Entre para usar o servidor Sofa. P2P e rede local funcionam sem conta.',
  'Automatic will use P2P through Cloudflare while the Sofa server is unavailable.':
    'O modo automático usará P2P pelo Cloudflare enquanto o servidor Sofa estiver indisponível.',
  'This sharing mode is unavailable. Choose another mode in settings or check again.':
    'Este modo de compartilhamento está indisponível. Escolha outro nas configurações ou verifique novamente.',
  'Update Sofa to use the server.': 'Atualize o Sofa para usar o servidor.',
  'The Sofa server is having account problems. Use P2P or Local network.':
    'O servidor Sofa está com problemas nas contas. Use P2P ou rede local.',
  'Hosting on the Sofa server is unavailable. Use P2P or Local network.':
    'O compartilhamento pelo servidor Sofa está indisponível. Use P2P ou rede local.',
  'The Sofa server address is invalid.': 'O endereço do servidor Sofa é inválido.',
  'The Sofa server cannot be reached. Use P2P or Local network.':
    'Não foi possível conectar ao servidor Sofa. Use P2P ou rede local.',
  'The Cloudflare helper is missing. Reinstall Sofa or use Local network.':
    'O auxiliar do Cloudflare está ausente. Reinstale o Sofa ou use rede local.',
  'Cloudflare cannot be reached. Try Local network.':
    'Não foi possível conectar ao Cloudflare. Tente a rede local.',
  'The viewer page is missing. Reinstall Sofa.':
    'A página de visualização está ausente. Reinstale o Sofa.',
  'Sofa cannot open a local sharing port.':
    'O Sofa não consegue abrir uma porta de compartilhamento local.',
  'Sofa server unavailable. Sharing through a tunnel with one upload per viewer. Send viewers the new link.':
    'Servidor do Sofa indisponível. Transmitindo por um túnel com um envio por espectador. Envie o novo link aos espectadores.',
  // Title bar and shared
  'Getting cozy…': 'Deixando tudo aconchegante…',
  'Try again': 'Tentar novamente',
  'Finding your seat…': 'Preparando seu lugar…',
  'Your seat is taking a little longer.': 'Seu lugar está demorando um pouquinho.',
  'Could not reconnect to your stream. Try again to recover the controls.':
    'Não foi possível reconectar à transmissão. Tente novamente para recuperar os controles.',
  Minimize: 'Minimizar',
  Maximize: 'Maximizar',
  Restore: 'Restaurar',
  Close: 'Fechar',
  'Protocol debug': 'Depuração do protocolo',

  // First run
  'Your window. Your friends. Nobody in between.': 'Sua janela. Seus amigos. Ninguém no meio.',
  'Share one window and its sound. Friends open a link in any browser: no app, no account.':
    'Compartilhe uma janela e o som dela. Seus amigos abrem um link em qualquer navegador: sem app, sem conta.',
  'Only the window you pick, never your whole screen':
    'Só a janela que você escolher, nunca a tela inteira',
  'Encrypted, straight from this PC to each friend':
    'Criptografado, direto deste PC para cada amigo',
  'Up to 4 friends, 1080p at 60 fps': 'Até 4 amigos, 1080p a 60 fps',
  'Where are your friends?': 'Onde estão seus amigos?',
  Anywhere: 'Qualquer lugar',
  'A public link through a temporary tunnel. No ports to open.':
    'Um link público por um túnel temporário. Sem abrir portas.',
  'Same Wi-Fi or house': 'Mesmo Wi-Fi ou casa',
  'Same Wi-Fi': 'Mesmo Wi-Fi',
  'Local network only. Nothing leaves your home.': 'Só na rede local. Nada sai da sua casa.',
  'You can change both later.': 'Você pode mudar os dois depois.',
  'Grab a seat →': 'Sente aqui →',

  // Picker
  'Pick a window': 'Escolha uma janela',
  'Go live': 'Transmitir',
  'Starting…': 'Iniciando…',
  'Creating invite link…': 'Criando link de convite…',
  'Opening room…': 'Abrindo sala…',
  'Preparing video…': 'Preparando vídeo…',
  'Step {n} of {total}': 'Etapa {n} de {total}',
  Progress: 'Progresso',
  'What are we watching?': 'O que vamos assistir?',
  'Find a window': 'Buscar uma janela',
  'Refresh windows': 'Atualizar janelas',
  Settings: 'Configurações',
  'Sofa {version} is ready.': 'O Sofa {version} está pronto.',
  'Updating…': 'Atualizando…',
  'Update and restart': 'Atualizar e reiniciar',
  'Checking…': 'Verificando…',
  'Check for updates': 'Buscar atualizações',
  'You are up to date.': 'Você está com a versão mais recente.',
  'No shareable windows found. Open the window you want to share, then refresh.':
    'Nenhuma janela para compartilhar. Abra a janela que quer compartilhar e atualize.',
  'All system audio': 'Todo o áudio do sistema',
  'Leave Discord out of the stream': 'Deixar o Discord fora da transmissão',
  'All system audio is shared; change this in settings.':
    'Todo o áudio do sistema é compartilhado; mude isso nas configurações.',
  'Minimized: it will be restored when you start': 'Minimizada: será restaurada ao iniciar',
  Minimized: 'Minimizada',
  'Playing sound': 'Tocando som',
  Quiet: 'Silenciosa',
  'No window matches “{query}”.': 'Nenhuma janela corresponde a “{query}”.',
  'Pick a window to share first.': 'Escolha uma janela para compartilhar primeiro.',
  Sharing: 'Compartilhando',
  ', with its sound': ', com o som dela',
  'Nothing picked yet': 'Nada escolhido ainda',
  'Switch who can join': 'Mudar quem pode entrar',
  'Sofa server': 'Servidor Sofa',
  Custom: 'Personalizado',
  Smooth: 'Fluido',
  Crisp: 'Nítido',
  'preset|Light': 'Leve',
  Light: 'Claro',

  // Stream settings sheet
  'How it looks': 'Como fica',
  'Close stream settings': 'Fechar configurações da transmissão',
  'Quality ceiling': 'Limite de qualidade',
  'With {n} seats filled you will upload about':
    'Com {n} lugares ocupados você vai enviar cerca de',
  "If someone's connection is slow, the picture gets softer for everyone.":
    'Se a conexão de alguém for lenta, a imagem fica mais suave para todos.',
  'Seats on the sofa': 'Lugares no sofá',
  'Each friend uses your upload': 'Cada amigo usa o seu envio',
  'Fewer seats': 'Menos lugares',
  'More seats': 'Mais lugares',
  'Sound from': 'Som de',
  'Same app as the window': 'Mesmo app da janela',
  '(silent)': '(silencioso)',
  'Include helper processes (needed for browsers)':
    'Incluir processos auxiliares (necessário para navegadores)',
  'Viewers connect through': 'Os espectadores conectam por',
  'Anywhere: public link through a temporary tunnel':
    'Qualquer lugar: link público por um túnel temporário',
  'Same Wi-Fi or house: local network only': 'Mesmo Wi-Fi ou casa: só na rede local',
  'The Sofa server': 'O servidor Sofa',
  Automatic: 'Automático',
  'P2P through Cloudflare': 'P2P pelo Cloudflare',
  'Local network': 'Rede local',
  'Uses the Sofa server when signed in, with Cloudflare P2P as fallback.':
    'Usa o servidor Sofa quando conectado à conta, com P2P pelo Cloudflare como alternativa.',
  'Connect directly to viewers using a public link. No ports to open.':
    'Conecte diretamente aos espectadores por um link público. Sem abrir portas.',
  'Room password': 'Senha da sala',
  '· optional': '· opcional',
  'Anyone with the link can join': 'Qualquer pessoa com o link pode entrar',
  Reset: 'Redefinir',
  Done: 'Pronto',

  // Live
  'Mini view': 'Mini janela',
  'Chat overlay': 'Chat sobre o jogo',
  'Chat overlay side': 'Lado do chat',
  Left: 'Esquerda',
  Right: 'Direita',
  'Where chat sits over the game and over the video you watch.':
    'Onde o chat fica sobre o jogo e sobre o vídeo que você assiste.',
  'Chat over video': 'Chat sobre o vídeo',
  'Translucent chat over your game, hidden from the stream.':
    'Chat translúcido sobre o jogo, invisível na transmissão.',
  'Typing key': 'Tecla para digitar',
  'Press the keys…': 'Pressione as teclas…',
  'Press this key in your game to type in the chat overlay.':
    'Pressione esta tecla no jogo para digitar no chat sobre o jogo.',
  'Ctrl+Alt types characters on many keyboards. Pick another combination.':
    'Ctrl+Alt digita caracteres em muitos teclados. Escolha outra combinação.',
  'Press {key} to type': 'Pressione {key} para digitar',
  'Chat over video (Enter to type)': 'Chat sobre o vídeo (Enter para digitar)',
  'Hide stats': 'Ocultar estatísticas',
  Stats: 'Estatísticas',
  'Stop sharing': 'Parar de compartilhar',
  'Live preview of your shared window': 'Prévia ao vivo da janela compartilhada',
  'live preview · {title}': 'prévia ao vivo · {title}',
  'your window': 'sua janela',
  'Smooth {fps} fps: your GPU is busy': 'Fluido a {fps} fps: sua GPU está ocupada',
  "Other video on this PC is using the GPU's video engine, so the stream runs at an even half rate instead of stuttering.":
    'Outro vídeo neste PC está usando o mecanismo de vídeo da GPU, então a transmissão roda na metade da taxa em vez de engasgar.',
  "Softer picture for {name}'s connection": 'Imagem mais suave por causa da conexão de {name}',
  'Softer picture for a slow connection': 'Imagem mais suave por causa de uma conexão lenta',
  'Looking great': 'Tudo ótimo',
  'Send this to your friends': 'Mande isto para seus amigos',
  code: 'código',
  Copied: 'Copiado',
  'Copy link': 'Copiar link',
  'Watch a friend': 'Assistir a um amigo',
  'Your own stream keeps running while you watch': 'Sua transmissão continua enquanto você assiste',
  "You're sharing a whole screen, so this room's sound can loop back to your own friends. Use headphones or mute here.":
    'Você está compartilhando a tela inteira, então o som desta sala pode voltar para os seus amigos. Use fones de ouvido ou silencie aqui.',
  'Back to sharing': 'Voltar a compartilhar',
  'Sharing instead?': 'Quer compartilhar?',
  'Share a window': 'Compartilhar uma janela',
  'Pick a window and send your friends a link.': 'Escolha uma janela e mande um link aos amigos.',
  'Join a room instead': 'Entrar em uma sala',
  'To share a window, open the Sofa desktop app.':
    'Para compartilhar uma janela, abra o app Sofa para desktop.',
  'A browser can watch a room but cannot capture your windows. Watching works here; sharing needs the app on Windows.':
    'O navegador pode assistir a uma sala, mas não captura suas janelas. Assistir funciona aqui; compartilhar exige o app no Windows.',
  'Sharing needs the Sofa desktop app on Windows: only the app can capture a window and encode it.':
    'Compartilhar exige o app Sofa para Windows: só ele captura e codifica uma janela.',
  Friends: 'Amigos',
  'Invite a friend': 'Convidar um amigo',
  Invited: 'Convidado',
  'Your friends': 'Seus amigos',
  Requests: 'Pedidos',
  Accept: 'Aceitar',
  Decline: 'Recusar',
  Remove: 'Remover',
  Watch: 'Assistir',
  Dismiss: 'Dispensar',
  'Sharing now': 'Compartilhando agora',
  Online: 'Online',
  Offline: 'Offline',
  'Connecting…': 'Conectando…',
  'Reconnecting…': 'Reconectando…',
  'No friends server set.': 'Nenhum servidor de amigos definido.',
  'Enter the address of a server below to see when friends are online.':
    'Digite abaixo o endereço de um servidor para ver quando os amigos estão online.',
  'Add {name} as a friend?': 'Adicionar {name} como amigo?',
  'They will get a request. You see each other online once they accept.':
    'Eles receberão um pedido. Vocês se veem online depois que aceitarem.',
  'Send request': 'Enviar pedido',
  'Not now': 'Agora não',
  '{name} invited you': '{name} convidou você',
  Someone: 'Alguém',
  'No friends yet. Send them your link below, or paste theirs.':
    'Nenhum amigo ainda. Mande seu link abaixo, ou cole o deles.',
  'Add a friend': 'Adicionar um amigo',
  'Switch window or screen': 'Trocar de janela ou tela',
  'Switch to': 'Trocar para',
  Screen: 'Tela',
  'Friends keep watching': 'Os amigos continuam assistindo',
  'Send link': 'Enviar link',
  On: 'Ligado',
  Off: 'Desligado',
  'Technical stats': 'Estatísticas técnicas',
  'When you go live': 'Quando você entra ao vivo',
  'Applies from the next stream': 'Vale a partir da próxima transmissão',
  'A small always-on-top window with viewers and chat.':
    'Uma janelinha sempre no topo com espectadores e chat.',
  'fps, bitrate and encode time over the preview.':
    'fps, bitrate e tempo de codificação sobre a prévia.',
  'Picked for you. Change only if streams stutter.':
    'Escolhido para você. Mude só se a transmissão travar.',
  'using your GPU': 'usando sua GPU',
  'software fallback': 'por software',
  App: 'App',
  'Also the page friends see': 'Também a página que seus amigos veem',
  'Edit profile': 'Editar perfil',
  'Delete account…': 'Excluir conta…',
  'handle changeable on {date}': 'usuário pode mudar em {date}',
  '{n} watching': '{n} assistindo',
  'Back to stream': 'Voltar à transmissão',
  Stop: 'Parar',
  "You're live.": 'Você está ao vivo.',
  '{n} on the sofa. Invite more from the list, or copy your link.':
    '{n} no sofá. Convide mais pela lista ou copie seu link.',
  'Nobody is on the sofa yet. Invite friends from the list, or copy your link.':
    'Ninguém no sofá ainda. Convide amigos pela lista ou copie seu link.',
  '{n} other friends sharing': '{n} outros amigos compartilhando',
  Pending: 'Pendentes',
  Asked: 'Pedido enviado',
  Tell: 'Avisar',
  '@handle or link': '@usuário ou link',
  'or send yours': 'ou envie o seu',
  'Account and server': 'Conta e servidor',
  'It only lets them ask to be your friend.': 'Ele só deixa a pessoa pedir para ser sua amiga.',
  'No friends yet. Add one and they will show up here.':
    'Nenhum amigo ainda. Adicione um e ele aparece aqui.',
  'Paste the link they sent you.': 'Cole o link que te enviaram.',
  'Friend link': 'Link do amigo',
  'That is not a friend link. Paste the whole link, or the long code.':
    'Isso não é um link de amigo. Cole o link inteiro, ou o código longo.',
  'That is you.': 'Esse é você.',
  'Your link': 'Seu link',
  'Send this to a friend. It only lets them ask to be your friend.':
    'Mande isto a um amigo. Só permite que ele peça para ser seu amigo.',
  'Your friend link': 'Seu link de amigo',
  Advanced: 'Avançado',
  'Hide advanced': 'Ocultar avançado',
  'Friends server': 'Servidor de amigos',
  Use: 'Usar',
  'Sign in to use friends': 'Entre para usar os amigos',
  'Friends, invites and seeing who is online need an account. Sharing and watching a window never do.':
    'Amigos, convites e ver quem está online exigem uma conta. Compartilhar e assistir uma janela nunca exigem.',
  'Sign in to add {name} as a friend.': 'Entre para adicionar {name} como amigo.',
  'Accounts are not set up in this build.': 'As contas não estão configuradas nesta versão.',
  'Signed in as {name}': 'Conectado como {name}',
  'your account': 'sua conta',
  'That is not a friend link. Paste the whole link you were sent.':
    'Isso não é um link de amigo. Cole o link inteiro que você recebeu.',
  Account: 'Conta',
  'Sign in to keep your friends when you move to a new PC.':
    'Entre para manter seus amigos ao trocar de PC.',
  'Signed in': 'Conectado à conta',
  'Linked to your friends': 'Ligada aos seus amigos',
  'Linking…': 'Ligando…',
  'Sign out': 'Sair da conta',
  'Sign in': 'Entrar',
  'Create account': 'Criar conta',
  'Continue with Google': 'Continuar com Google',
  'Continue with Discord': 'Continuar com Discord',
  Email: 'E-mail',
  'Check your email to confirm, then sign in.': 'Confirme pelo e-mail e depois entre.',
  'Your identity is a key kept on this device. Back it up to keep your friends if you reinstall.':
    'Sua identidade é uma chave guardada neste dispositivo. Faça um backup para manter seus amigos se reinstalar.',
  'Show backup key': 'Mostrar chave de backup',
  'Hide backup key': 'Ocultar chave de backup',
  'Backup key': 'Chave de backup',
  'Anyone with this key can sign in as you. Keep it private.':
    'Qualquer pessoa com esta chave pode entrar como você. Mantenha em segredo.',
  'Paste a backup key to restore': 'Cole uma chave de backup para restaurar',
  'Restore key': 'Restaurar chave',
  'That is not a backup key.': 'Isso não é uma chave de backup.',
  'Remove {name}': 'Remover {name}',
  Watching: 'Assistindo',
  '{a} of {b}': '{a} de {b}',
  'joining…': 'entrando…',
  'Nobody yet. Send them the link.': 'Ninguém ainda. Mande o link.',
  'Great connection': 'Conexão ótima',
  'Good connection': 'Conexão boa',
  'Weak connection': 'Conexão fraca',
  'Poor connection': 'Conexão ruim',

  // Chat
  Chat: 'Chat',
  'No messages yet.': 'Nenhuma mensagem ainda.',
  'Say something': 'Diga algo',
  'Chat message': 'Mensagem do chat',
  Send: 'Enviar',

  // Settings page
  Back: 'Voltar',
  'Your name': 'Seu nome',
  'Shown to friends in chat': 'Mostrado aos amigos no chat',
  Language: 'Idioma',
  'App and the page friends see': 'O app e a página que os amigos veem',
  Appearance: 'Aparência',
  'Light unless you choose otherwise': 'Claro, a menos que você escolha outro',
  Windows: 'Windows',
  Dark: 'Escuro',
  'Switch to dark mode': 'Mudar para o modo escuro',
  'Switch to light mode': 'Mudar para o modo claro',
  'Show the mini view when I go live': 'Mostrar a mini janela quando eu começar a transmitir',
  'A small always-on-top window with viewers and chat':
    'Uma janela pequena sempre no topo com espectadores e chat',
  'Show technical stats': 'Mostrar estatísticas técnicas',
  'fps, bitrate and encode time on the preview': 'fps, bitrate e tempo de codificação na prévia',
  'Video encoder': 'Codificador de vídeo',
  Auto: 'Auto',
  'Media Foundation': 'Media Foundation',
  'Auto picked {encoder} for this GPU': 'O modo Auto escolheu {encoder} para esta GPU',
  'Applies the next time you go live': 'Vale na próxima vez que você transmitir',
  'Accounts are optional. P2P video goes straight from this PC to your friends.':
    'Contas são opcionais. O vídeo P2P vai direto deste PC para seus amigos.',
  protocol: 'protocolo',

  // Mini view
  Slow: 'Lenta',
  Busy: 'Ocupada',
  Great: 'Ótima',
  'Nobody yet': 'Ninguém ainda',
  '{n} on the sofa': '{n} no sofá',
  'Open Sofa': 'Abrir o Sofa',
  'Close mini view': 'Fechar mini janela',
  'ms encode': 'ms codif.',
  dropped: 'perdidos',
  'Waiting for the stream…': 'Aguardando a transmissão…',

  // Home, 404
  'Share one window with a few friends': 'Compartilhe uma janela com alguns amigos',
  'Join a room': 'Entrar em uma sala',
  'Paste the link you were sent, or type the code.': 'Cole o link que recebeu ou digite o código.',
  'Room code': 'Código da sala',
  Join: 'Entrar',
  'Hosting instead?': 'Quer transmitir?',
  'Open the host panel': 'Abrir o painel do anfitrião',
  'Nothing here': 'Não há nada aqui',
  'Back to the start': 'Voltar ao início',

  // Viewer
  'Not connected': 'Não conectado',
  Connecting: 'Conectando',
  Connected: 'Conectado',
  Reconnecting: 'Reconectando',
  Disconnected: 'Desconectado',
  '{name} is sharing': '{name} está compartilhando',
  'Room {code}': 'Sala {code}',
  'Leave the sofa': 'Sair do sofá',
  'Fill in your name to join the room.': 'Digite seu nome para entrar na sala.',
  'Connected.': 'Conectado.',
  'Hosted by {name} · {n} in the room': 'Anfitrião: {name} · {n} na sala',
  'In the room. Waiting for the host to send video.':
    'Na sala. Aguardando o anfitrião enviar o vídeo.',
  'Connecting to the host…': 'Conectando ao anfitrião…',
  'The connection to the host failed; reconnecting…':
    'A conexão com o anfitrião falhou; reconectando…',
  'Connection interrupted; recovering…': 'Conexão interrompida; recuperando…',
  'Cannot reach the host. Retrying… If sharing restarted, ask the host for their current link.':
    'Não foi possível conectar ao anfitrião. Tentando novamente… Se o compartilhamento reiniciou, peça o link atual ao anfitrião.',
  'Reconnecting to the host (attempt {n})…': 'Reconectando ao anfitrião (tentativa {n})…',
  Unmute: 'Ativar som',
  Password: 'Senha',
  '(only if the host set one)': '(só se o anfitrião definiu uma)',
  'Joining…': 'Entrando…',
  'Join room': 'Entrar na sala',
  'Try another code': 'Tentar outro código',
  Guest: 'Convidado',

  "{name}'s sofa": 'Sofá de {name}',
  Bumpy: 'Instável',
  Struggling: 'Com problemas',
  'Stream health': 'Qualidade da transmissão',
  'Smooth stream': 'Transmissão fluida',
  'Bumpy stream': 'Transmissão instável',
  'Struggling stream': 'Transmissão com problemas',
  Path: 'Caminho',
  'Direct (peer-to-peer)': 'Direto (ponto a ponto)',
  'Through a relay': 'Por um servidor de retransmissão',
  Delay: 'Atraso',
  'Frame rate': 'Taxa de quadros',
  Resolution: 'Resolução',
  'Dropped frames': 'Quadros perdidos',
  'Technical details on the video': 'Detalhes técnicos sobre o vídeo',
  LIVE: 'AO VIVO',
  Mute: 'Silenciar',
  Volume: 'Volume',
  'In sync with live': 'Sincronizado com o ao vivo',
  'Catching up': 'Alcançando',
  'Picture in picture': 'Imagem em imagem',
  'Theater mode': 'Modo cinema',
  'Point and draw': 'Apontar e desenhar',
  draw: 'desenhar',
  pointer: 'apontar',
  ping: 'chamar atenção',
  'Hold D or L. Ctrl+Shift+M to stop.': 'Segure D ou L. Ctrl+Shift+M para parar.',
  'Point and draw (D): move to point, click to ping, drag to draw':
    'Apontar e desenhar (D): mova para apontar, clique para chamar atenção, arraste para desenhar',
  'Full screen': 'Tela cheia',
  'Exit full screen': 'Sair da tela cheia',
  'You joined {ago}': 'Você entrou {ago}',
  'just now': 'agora mesmo',
  '{n} min ago': 'há {n} min',
  Heart: 'Coração',
  Cheer: 'Comemorar',
  Laugh: 'Rir',
  Fire: 'Fogo',
  Aww: 'Aww',
  'Raise hand': 'Levantar a mão',
  'Lower hand': 'Baixar a mão',
  'On the sofa': 'No sofá',
  Invite: 'Convidar',
  'Link copied': 'Link copiado',
  host: 'anfitrião',
  'Hand raised': 'Mão levantada',
  '1 hand up': '1 mão levantada',
  '{n} hands up': '{n} mãos levantadas',
  You: 'Você',
  Reactions: 'Reações',
  'sent a heart': 'mandou um coração',
  cheered: 'comemorou',
  'is laughing': 'está rindo',
  'is on fire': 'está pegando fogo',
  'is sad': 'está triste',
  'raised a hand': 'levantou a mão',
  'lowered their hand': 'baixou a mão',

  // Tray
  'Not sharing': 'Não compartilhando',
  'Live · {seats} · {time}': 'Ao vivo · {seats} · {time}',
  'Copy invite link': 'Copiar link do convite',
  'Show mini view': 'Mostrar mini janela',
  Quit: 'Sair',

  // UI v4
  'Got a link?': 'Tem um link?',
  'Paste a link or room code': 'Cole um link ou código da sala',
  'Put something on the sofa.': 'Coloque algo no sofá.',
  'One window and its sound, straight to friends. They just open a link.':
    'Uma janela e o som dela, direto para os amigos. Eles só abrem um link.',
  'last shared window': 'última janela compartilhada',
  'Live now': 'Ao vivo agora',
  '{n} friends sharing': '{n} amigos compartilhando',
  'Better with friends.': 'Melhor com amigos.',
  'Sign in once and your sofa follows you.': 'Entre uma vez e seu sofá vai com você.',
  'Works without an account': 'Funciona sem conta',
  'Nobody online right now.': 'Ninguém online agora.',
  'When a friend starts sharing, it shows up here.':
    'Quando um amigo começar a compartilhar, aparece aqui.',
  '{n} open seats': '{n} lugares livres',
  'Share one window with a few friends.': 'Compartilhe uma janela com alguns amigos.',
  'Send a link. They open it in any browser. One window, its sound, 1080p at 60 fps. Peer to peer and encrypted.':
    'Mande um link. Eles abrem em qualquer navegador. Uma janela, o som dela, 1080p a 60 fps. Ponto a ponto e criptografado.',
  'See who is online and sharing': 'Veja quem está online e compartilhando',
  'Invite friends in one click': 'Convide amigos com um clique',
  'Keep your friends on a new PC': 'Mantenha seus amigos em um PC novo',
  Add: 'Adicionar',
  'Step 1 of 2': 'Passo 1 de 2',
  'Share a whole screen instead': 'Compartilhar a tela inteira',
  Sound: 'Som',
  'with sound': 'com som',
  'Pick a window above': 'Escolha uma janela acima',
  Handle: 'Usuário',
  'Display name': 'Nome de exibição',
  'Welcome. Pick your handle': 'Bem-vindo. Escolha seu usuário',
  'Friends find you by your handle. You can change both later in Settings.':
    'Seus amigos encontram você pelo usuário. Você pode mudar os dois depois, em Configurações.',
  'What should friends call you?': 'Como seus amigos devem te chamar?',
  Continue: 'Continuar',
  'Saving…': 'Salvando…',
  'This is your handle': 'Este é o seu usuário',
  '@{handle} is available': '@{handle} está disponível',
  '@{handle} is taken': '@{handle} já está em uso',
  'Use @{handle}': 'Usar @{handle}',
  'That handle is not allowed.': 'Esse usuário não é permitido.',
  'Use at least 3 characters': 'Use pelo menos 3 caracteres',
  'Use at most 20 characters': 'Use no máximo 20 caracteres',
  'Use only letters, numbers, _ and .': 'Use só letras, números, _ e .',
  'Start and end with a letter or number': 'Comece e termine com uma letra ou número',
  'No two dots in a row': 'Sem dois pontos seguidos',
  'That name is reserved': 'Esse nome é reservado',
  'Someone already has that handle.': 'Alguém já usa esse usuário.',
  'You changed your handle recently. Try again later.':
    'Você mudou seu usuário há pouco tempo. Tente de novo mais tarde.',
  'Not connected to the friends server yet.': 'Ainda sem conexão com o servidor de amigos.',
  'The server did not answer. Try again.': 'O servidor não respondeu. Tente de novo.',
  'No handle yet': 'Sem usuário ainda',
  'Shown to friends and in rooms': 'Mostrado aos amigos e nas salas',
  'How friends find you': 'Como seus amigos encontram você',
  'You can change it again on {date}': 'Você poderá mudar de novo em {date}',
  Save: 'Salvar',
  Cancel: 'Cancelar',
  Change: 'Mudar',
  Choose: 'Escolher',
  'Search their @handle, or paste a link they sent you.':
    'Busque o @usuário deles ou cole um link que enviaram.',
  'Type their @handle, or paste the link they sent you.':
    'Digite o @usuário deles ou cole o link que enviaram.',
  'Friend handle or link': 'Usuário ou link do amigo',
  Search: 'Buscar',
  'Nobody has that handle.': 'Ninguém tem esse usuário.',
  'Cannot reach the friends server at {url}. Is it running?':
    'Não consegui falar com o servidor de amigos em {url}. Ele está rodando?',
  'Already friends': 'Já são amigos',
  'Request sent': 'Pedido enviado',
  'Add friend': 'Adicionar amigo',
  'No friends yet. Search for their @handle below, or send them your link.':
    'Nenhum amigo ainda. Busque o @usuário deles abaixo ou envie seu link.',
}

const dictionaries: Record<Language, Record<string, string>> = { en: {}, 'pt-BR': ptBR }

/** Recognize deliberate product copy in either language, including English keys. */
export function isKnownMessage(text: string): boolean {
  return Object.hasOwn(ptBR, text) || Object.values(ptBR).includes(text)
}

/** Translate `text` into the current language, filling `{name}` placeholders. */
export function t(text: string, vars?: Record<string, string | number>, context?: string): string {
  const dictionary = dictionaries[prefs.language]
  const translated = (context && dictionary?.[`${context}|${text}`]) ?? dictionary?.[text] ?? text
  if (!vars) return translated
  return translated.replace(/\{(\w+)\}/g, (whole, key: string) =>
    key in vars ? String(vars[key]) : whole,
  )
}
